import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Sector,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
} from 'recharts';
import { V_URL } from '../../../../BaseUrl';
import { getProject } from '../../../../Store/Store/Project/Project';
import { Package, Layers, HelpCircle, FolderOpen, Info, ArrowUpRight, Download } from 'lucide-react';

// ─── Colour palette ────────────────────────────────────────────────────────────
const PIE_COLORS = [
    '#ff6700', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#22C55E', '#6366F1',
    '#84CC16', '#A855F7', '#0EA5E9', '#E11D48', '#D97706',
    '#14B8A6', '#C026D3', '#059669', '#DC2626', '#4F46E5',
];
const OTHERS_COLOR = '#94A3B8';
const TOP_N_PIE = 15;
const TOP_N_BAR = 20;

// ─── Chart Download Helper (High-res PNG export) ──────────────────────────────
const downloadChartAsPng = (containerRef, filename = 'chart.png', title = '') => {
    if (!containerRef?.current) return;
    const svg = containerRef.current.querySelector('svg.recharts-surface') || containerRef.current.querySelector('svg');
    if (!svg) return;

    try {
        const bbox = svg.getBoundingClientRect();
        const width = bbox.width || 600;
        const height = bbox.height || 360;
        const scale = 2; // 2x for sharp retina resolution
        const titleHeight = title ? 40 : 0;

        // Clone SVG and set required XML attributes
        const clone = svg.cloneNode(true);
        clone.setAttribute('version', '1.1');
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        clone.setAttribute('width', width);
        clone.setAttribute('height', height);

        // Serialize SVG to XML string
        const svgString = new XMLSerializer().serializeToString(clone);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const URLObj = window.URL || window.webkitURL || window;
        const blobURL = URLObj.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width * scale;
            canvas.height = (height + titleHeight) * scale;
            const ctx = canvas.getContext('2d');

            // White Background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Title Header
            if (title) {
                ctx.fillStyle = '#0f172a';
                ctx.font = `bold ${13 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
                ctx.fillText(title, 16 * scale, 24 * scale);

                // Divider line
                ctx.strokeStyle = '#f1f5f9';
                ctx.lineWidth = 1 * scale;
                ctx.beginPath();
                ctx.moveTo(16 * scale, 34 * scale);
                ctx.lineTo((width - 16) * scale, 34 * scale);
                ctx.stroke();
            }

            // Draw SVG
            ctx.drawImage(img, 0, titleHeight * scale, width * scale, height * scale);

            // Trigger download
            const a = document.createElement('a');
            a.download = filename;
            a.href = canvas.toDataURL('image/png');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URLObj.revokeObjectURL(blobURL);
        };
        img.src = blobURL;
    } catch (err) {
        console.error('Error downloading chart:', err);
    }
};

// ─── Draw Rounded Rectangle Helper (Cross-browser safe) ──────────────────────
const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, radius);
        return;
    }
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
};

// ─── Download Pie Chart with Table Format Layout (High-Res PNG) ──────────────
const downloadPieChartWithLeaderLines = (
    pieData,
    filename = 'item_wise_purchase_chart.png',
    title = 'Item Wise Purchase Receiving',
    totalOverallQty = 0,
    totalItemsCount = 0,
    rawAllItems = []
) => {
    if (!pieData || pieData.length === 0) return;

    // Use full items list if available (shows all items), otherwise pieData
    const tableItems = (rawAllItems && rawAllItems.length > 0) ? rawAllItems : pieData;
    const isDualCol = tableItems.length > 16;
    const rowCount = tableItems.length;
    const halfCount = isDualCol ? Math.ceil(rowCount / 2) : rowCount;

    const scale = 2;
    const logicalW = isDualCol ? 1380 : 1200;
    const rowH = isDualCol ? 26 : (rowCount <= 5 ? 42 : rowCount <= 8 ? 36 : rowCount <= 12 ? 32 : 28);
    const tableBodyH = halfCount * rowH;
    const logicalH = Math.max(650, 140 + tableBodyH);

    const canvasWidth = logicalW * scale;
    const canvasHeight = logicalH * scale;
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    // 1. Clean white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 2. Header Title & Subtitle
    ctx.textAlign = 'left';
    ctx.fillStyle = '#0f172a';
    ctx.font = `bold ${15 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.fillText(title, 28 * scale, 34 * scale);

    ctx.fillStyle = '#64748b';
    ctx.font = `500 ${10.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.fillText(`Quantity breakdown of all ${tableItems.length} purchased items with percentage share`, 28 * scale, 52 * scale);

    // Top-Right KPI Badges in Header
    const displayQty = totalOverallQty || tableItems.reduce((s, it) => s + Number(it.total_qty || 0), 0);
    const displayItems = totalItemsCount || tableItems.length;

    const b1Text = `${Number(displayQty).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Total Qty`;
    const b2Text = `${displayItems} Items`;

    ctx.font = `600 ${10 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    const b1W = ctx.measureText(b1Text).width + 20 * scale;
    const b2W = ctx.measureText(b2Text).width + 20 * scale;

    const b2X = (logicalW - 28) * scale - b2W;
    const b1X = b2X - 10 * scale - b1W;
    const badgeY = 22 * scale;
    const badgeH = 26 * scale;

    // Badge 1 (Believe Orange)
    ctx.fillStyle = '#fff7f1';
    drawRoundedRect(ctx, b1X, badgeY, b1W, badgeH, 6 * scale);
    ctx.fill();
    ctx.strokeStyle = '#fed7aa';
    ctx.lineWidth = 1 * scale;
    ctx.stroke();
    ctx.fillStyle = '#ea580c';
    ctx.fillText(b1Text, b1X + 10 * scale, badgeY + 17 * scale);

    // Badge 2 (Green)
    ctx.fillStyle = '#f0fdf4';
    drawRoundedRect(ctx, b2X, badgeY, b2W, badgeH, 6 * scale);
    ctx.fill();
    ctx.strokeStyle = '#bbf7d0';
    ctx.lineWidth = 1 * scale;
    ctx.stroke();
    ctx.fillStyle = '#15803d';
    ctx.fillText(b2Text, b2X + 10 * scale, badgeY + 17 * scale);

    // Header Divider Line
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = 1.2 * scale;
    ctx.beginPath();
    ctx.moveTo(28 * scale, 68 * scale);
    ctx.lineTo((logicalW - 28) * scale, 68 * scale);
    ctx.stroke();

    // 3. Left Side: Pie Chart Calculations
    const cx = (isDualCol ? 210 : 225) * scale;
    const cy = (logicalH / 2 + 30) * scale;
    const radius = (isDualCol ? 120 : 115) * scale;

    const sumQty = pieData.reduce((s, it) => s + Number(it.total_qty || 0), 0);
    let currentAngle = -Math.PI / 2; // 12 o'clock

    // Draw Slices
    const slicesWithAngles = pieData.map((slice, index) => {
        const sliceAngle = sumQty > 0 ? (Number(slice.total_qty || 0) / sumQty) * 2 * Math.PI : 0;
        const startAngle = currentAngle;
        const endAngle = currentAngle + sliceAngle;
        const midAngle = currentAngle + sliceAngle / 2;
        const color = slice._isOthers ? OTHERS_COLOR : PIE_COLORS[index % PIE_COLORS.length];

        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * scale;
        ctx.stroke();

        currentAngle = endAngle;
        return {
            ...slice,
            _startAngle: startAngle,
            _endAngle: endAngle,
            _midAngle: midAngle,
            _color: color,
        };
    });

    // Center Donut Cutout
    ctx.beginPath();
    ctx.arc(cx, cy, 40 * scale, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    // Center Donut Text
    ctx.textAlign = 'center';
    ctx.fillStyle = '#64748b';
    ctx.font = `500 ${9 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.fillText('TOTAL QTY', cx, cy - 4 * scale);
    ctx.fillStyle = '#0f172a';
    ctx.font = `bold ${11.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.fillText(Number(displayQty).toLocaleString('en-IN', { maximumFractionDigits: 2 }), cx, cy + 12 * scale);

    // Clean leader lines on Pie for Primary Slices (>= 5.0% or all if <= 4 items)
    const majorSlices = slicesWithAngles.filter(s => Number(s.percentage) >= 5.0 || pieData.length <= 4);
    majorSlices.forEach(s => {
        const cos = Math.cos(s._midAngle);
        const sin = Math.sin(s._midAngle);
        const isRight = cos >= 0;
        const sx = cx + (radius + 4 * scale) * cos;
        const sy = cy + (radius + 4 * scale) * sin;
        const mx = isRight ? (cx + radius + 18 * scale) : (cx - radius - 18 * scale);
        const ex = isRight ? (mx + 14 * scale) : (mx - 14 * scale);
        const ey = cy + (radius + 18 * scale) * sin;

        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(mx, ey);
        ctx.lineTo(ex, ey);
        ctx.strokeStyle = s._color;
        ctx.lineWidth = 1.4 * scale;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(ex, ey, 2.2 * scale, 0, 2 * Math.PI);
        ctx.fillStyle = s._color;
        ctx.fill();

        // Clean percentage pill at leader line
        const pctStr = `${s.percentage}%`;
        ctx.font = `bold ${8.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
        const pW = ctx.measureText(pctStr).width + 10 * scale;
        const pH = 17 * scale;
        const pX = isRight ? ex + 4 * scale : ex - 4 * scale - pW;
        const pY = ey - pH / 2;

        ctx.fillStyle = '#ffffff';
        drawRoundedRect(ctx, pX, pY, pW, pH, 4 * scale);
        ctx.fill();
        ctx.strokeStyle = s._color;
        ctx.lineWidth = 1.2 * scale;
        ctx.stroke();

        ctx.fillStyle = s._color;
        ctx.textAlign = 'center';
        ctx.fillText(pctStr, pX + pW / 2, ey + 3 * scale);
    });

    // 4. Right Side: Table Format (Dual Column if > 16 items, Single Column if <= 16)
    const startTableX = (isDualCol ? 420 : 450) * scale;
    const totalTableW = (logicalW * scale) - startTableX - 26 * scale;
    const headerH = 32 * scale;
    const footerH = 30 * scale;
    const tableTotalH = headerH + (halfCount * rowH * scale) + footerH;

    const drawSubTable = (subX, subW, itemsList, startIdx, showFooter) => {
        // Table Outer Card Border
        ctx.fillStyle = '#ffffff';
        drawRoundedRect(ctx, subX, 82 * scale, subW, tableTotalH, 8 * scale);
        ctx.fill();
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1 * scale;
        ctx.stroke();

        // Header Background
        ctx.fillStyle = '#f8fafc';
        ctx.beginPath();
        ctx.moveTo(subX + 8 * scale, 82 * scale);
        ctx.lineTo(subX + subW - 8 * scale, 82 * scale);
        ctx.quadraticCurveTo(subX + subW, 82 * scale, subX + subW, 82 * scale + 8 * scale);
        ctx.lineTo(subX + subW, 82 * scale + headerH);
        ctx.lineTo(subX, 82 * scale + headerH);
        ctx.lineTo(subX, 82 * scale + 8 * scale);
        ctx.quadraticCurveTo(subX, 82 * scale, subX + 8 * scale, 82 * scale);
        ctx.closePath();
        ctx.fill();

        // Header Bottom Border
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1.2 * scale;
        ctx.beginPath();
        ctx.moveTo(subX, 82 * scale + headerH);
        ctx.lineTo(subX + subW, 82 * scale + headerH);
        ctx.stroke();

        // Column Widths
        const sc0W = 34 * scale;
        const sc3W = (isDualCol ? 70 : 92) * scale;
        const sc2W = (isDualCol ? 110 : 145) * scale;
        const sc1W = subW - sc0W - sc2W - sc3W;

        const sx0 = subX;
        const sx1 = sx0 + sc0W;
        const sx2 = sx1 + sc1W;
        const sx3 = sx2 + sc2W;
        const sxEnd = subX + subW;

        // Header Titles
        ctx.fillStyle = '#475569';
        ctx.font = `bold ${9 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;

        ctx.textAlign = 'center';
        ctx.fillText('#', sx0 + sc0W / 2, 82 * scale + 20 * scale);

        ctx.textAlign = 'left';
        ctx.fillText('ITEM NAME', sx1 + 8 * scale, 82 * scale + 20 * scale);

        ctx.textAlign = 'right';
        ctx.fillText(isDualCol ? 'QTY' : 'QUANTITY', sx3 - 8 * scale, 82 * scale + 20 * scale);
        ctx.fillText(isDualCol ? 'SHARE' : 'SHARE %', sxEnd - 10 * scale, 82 * scale + 20 * scale);

        // Table Rows
        itemsList.forEach((item, i) => {
            const rY = 82 * scale + headerH + (i * rowH * scale);
            const isEven = i % 2 === 1;

            if (isEven) {
                ctx.fillStyle = '#f8fafc';
                ctx.fillRect(subX + 1 * scale, rY, subW - 2 * scale, rowH * scale);
            }

            ctx.strokeStyle = '#f1f5f9';
            ctx.lineWidth = 1 * scale;
            ctx.beginPath();
            ctx.moveTo(subX, rY + rowH * scale);
            ctx.lineTo(sxEnd, rY + rowH * scale);
            ctx.stroke();

            const textY = rY + (rowH * scale) / 2 + 3.5 * scale;

            // Col 0: #
            ctx.textAlign = 'center';
            ctx.fillStyle = '#94a3b8';
            ctx.font = `600 ${8 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            ctx.fillText(String(startIdx + i + 1), sx0 + sc0W / 2, textY);

            // Col 1: Color swatch + Item Name
            const color = item._isOthers ? OTHERS_COLOR : PIE_COLORS[(startIdx + i) % PIE_COLORS.length];
            ctx.beginPath();
            ctx.arc(sx1 + 9 * scale, rY + (rowH * scale) / 2, 3.5 * scale, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();

            ctx.textAlign = 'left';
            ctx.fillStyle = item._isOthers ? '#475569' : '#0f172a';
            ctx.font = `${item._isOthers ? 'bold' : '600'} ${8.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            const maxChars = isDualCol ? 24 : 38;
            const rawName = item.displayName || item.item_name || '';
            const name = rawName.length > maxChars ? rawName.slice(0, maxChars - 2) + '…' : rawName;
            ctx.fillText(name, sx1 + 18 * scale, textY);

            // Col 2: Quantity & Unit
            ctx.textAlign = 'right';
            ctx.fillStyle = '#334155';
            ctx.font = `600 ${8.5 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            const qtyText = `${Number(item.total_qty || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} ${item.unit || ''}`.trim();
            ctx.fillText(qtyText, sx3 - 8 * scale, textY);

            // Col 3: Share Badge
            const pctStr = `${item.percentage || 0}%`;
            ctx.font = `bold ${8 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            const pillW = ctx.measureText(pctStr).width + 10 * scale;
            const pillH = 16 * scale;
            const pillX = sxEnd - pillW - 8 * scale;
            const pillY = rY + (rowH * scale - pillH) / 2;

            ctx.fillStyle = item._isOthers ? '#f1f5f9' : '#fff7f1';
            drawRoundedRect(ctx, pillX, pillY, pillW, pillH, 3.5 * scale);
            ctx.fill();

            ctx.fillStyle = item._isOthers ? '#475569' : '#ea580c';
            ctx.textAlign = 'center';
            ctx.fillText(pctStr, pillX + pillW / 2, pillY + 11 * scale);
        });

        // Table Footer
        const fY = 82 * scale + headerH + (halfCount * rowH * scale);
        ctx.fillStyle = '#f1f5f9';
        ctx.beginPath();
        ctx.moveTo(subX, fY);
        ctx.lineTo(sxEnd, fY);
        ctx.lineTo(sxEnd, fY + footerH - 8 * scale);
        ctx.quadraticCurveTo(sxEnd, fY + footerH, sxEnd - 8 * scale, fY + footerH);
        ctx.lineTo(subX + 8 * scale, fY + footerH);
        ctx.quadraticCurveTo(subX, fY + footerH, subX, fY + footerH - 8 * scale);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1.2 * scale;
        ctx.beginPath();
        ctx.moveTo(subX, fY);
        ctx.lineTo(sxEnd, fY);
        ctx.stroke();

        if (showFooter) {
            const fTextY = fY + footerH / 2 + 3.5 * scale;
            ctx.textAlign = 'left';
            ctx.fillStyle = '#0f172a';
            ctx.font = `bold ${9 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            ctx.fillText('Total', sx1 + 8 * scale, fTextY);

            ctx.textAlign = 'right';
            ctx.fillText(Number(displayQty).toLocaleString('en-IN', { maximumFractionDigits: 2 }), sx3 - 8 * scale, fTextY);

            const totalPctStr = '100.0%';
            ctx.font = `bold ${8 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
            const fpW = ctx.measureText(totalPctStr).width + 10 * scale;
            const fpH = 16 * scale;
            const fpX = sxEnd - fpW - 8 * scale;
            const fpY = fY + (footerH - fpH) / 2;

            ctx.fillStyle = '#e2e8f0';
            drawRoundedRect(ctx, fpX, fpY, fpW, fpH, 3.5 * scale);
            ctx.fill();

            ctx.fillStyle = '#0f172a';
            ctx.textAlign = 'center';
            ctx.fillText(totalPctStr, fpX + fpW / 2, fpY + 11 * scale);
        }
    };

    if (isDualCol) {
        const gap = 16 * scale;
        const singleTableW = (totalTableW - gap) / 2;
        const col1Items = tableItems.slice(0, halfCount);
        const col2Items = tableItems.slice(halfCount);
        drawSubTable(startTableX, singleTableW, col1Items, 0, false);
        drawSubTable(startTableX + singleTableW + gap, singleTableW, col2Items, halfCount, true);
    } else {
        drawSubTable(startTableX, totalTableW, tableItems, 0, true);
    }

    // 5. Trigger PNG Download
    const a = document.createElement('a');
    a.download = filename;
    a.href = canvas.toDataURL('image/png');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

// ══════════════════════════════════════════════════════════════════════════════
// PIE CHART — Active shape + Tooltip
// ══════════════════════════════════════════════════════════════════════════════

const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 8) * cos;
    const sy = cy + (outerRadius + 8) * sin;
    const mx = cx + (outerRadius + 26) * cos;
    const my = cy + (outerRadius + 26) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 18;
    const ey = my;
    const anchor = cos >= 0 ? 'start' : 'end';
    const nameRaw = payload.displayName || payload.item_name || 'Item';
    const name = nameRaw.length > 22 ? nameRaw.slice(0, 20) + '…' : nameRaw;
    const pct = ((percent || 0) * 100).toFixed(1);
    return (
        <g>
            <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 10}
                startAngle={startAngle} endAngle={endAngle} fill={fill}
                style={{ filter: `drop-shadow(0 4px 14px ${fill}55)` }} />
            <Sector cx={cx} cy={cy} startAngle={startAngle} endAngle={endAngle}
                innerRadius={outerRadius + 13} outerRadius={outerRadius + 16} fill={fill} opacity={0.4} />
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={1.8} strokeLinecap="round" />
            <circle cx={ex} cy={ey} r={3.5} fill={fill} stroke="#fff" strokeWidth={2} />
            <text x={ex + (cos >= 0 ? 9 : -9)} y={ey - 6} textAnchor={anchor} fill="#1e293b" fontSize={12.5} fontWeight="700">{name}</text>
            <text x={ex + (cos >= 0 ? 9 : -9)} y={ey + 10} textAnchor={anchor} fill="#64748b" fontSize={11.5} fontWeight="500">
                {`${Number(value || 0).toLocaleString('en-IN')} ${payload.unit || ''} · ${pct}%`}
            </text>
        </g>
    );
};

const PieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const color = payload[0].fill || '#ff6700';
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.1)', padding: '12px 16px', minWidth: 200, fontSize: 13, zIndex: 1000 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.displayName || d.item_name}</span>
            </div>
            {d._isOthers ? (
                <div style={{ color: '#64748b', fontSize: 12 }}><strong style={{ color: '#0f172a' }}>{d._othersCount}</strong> items grouped</div>
            ) : d.item_code ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', marginBottom: 6, fontSize: 12 }}>
                    <span>Code</span><span style={{ fontWeight: 600, color: '#64748b' }}>{d.item_code}</span>
                </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: 6 }}>
                <span>Purchased Qty</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{Number(d.total_qty || 0).toLocaleString('en-IN')} {d.unit || ''}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                <span>Share</span>
                <span style={{ background: '#fff5ed', color: '#ff6700', fontWeight: 700, borderRadius: 20, padding: '2px 9px', fontSize: 11.5 }}>{d.percentage}%</span>
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
// BAR CHART — Custom Tooltip for Issued Items
// ══════════════════════════════════════════════════════════════════════════════
const IssueBarTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '14px 18px', minWidth: 230, maxWidth: 300, fontSize: 13, zIndex: 1000 }}>
            <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {d.fullName || d.item_name}
            </div>
            {d.item_code && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', marginBottom: 6, fontSize: 12 }}>
                    <span>Item Code</span>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{d.item_code}</span>
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: 6 }}>
                <span>Issued Qty</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: 14 }}>
                    {Number(d.total_qty || 0).toLocaleString('en-IN')} {d.unit || ''}
                </span>
            </div>
            {d.issue_count > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: 6, fontSize: 12 }}>
                    <span>Issue Vouchers</span>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{d.issue_count}</span>
                </div>
            )}
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
// Shared project options hook
// ══════════════════════════════════════════════════════════════════════════════
function useProjectOptions() {
    const dispatch = useDispatch();
    const projectState = useSelector((state) => state?.getProject?.user?.data);
    useEffect(() => {
        if (!projectState || projectState.length === 0) dispatch(getProject());
    }, [dispatch, projectState]);
    return useMemo(() => {
        const list = Array.isArray(projectState) ? projectState : [];
        return list.map((p) => ({
            label: `${p.name || 'Unnamed'} ${p.project_no ? `(${p.project_no})` : ''}`.trim(),
            value: p._id,
        }));
    }, [projectState]);
}

// ══════════════════════════════════════════════════════════════════════════════
// PIE CHART PANEL — Item Wise Purchase Receiving
// ══════════════════════════════════════════════════════════════════════════════
const PiePurchasePanel = ({ projectOptions }) => {
    const [project, setProject] = useState(null);
    const [data, setData] = useState({ total_purchased_qty: 0, total_items_count: 0, items: [] });
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const chartContainerRef = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.post(
                `${V_URL}/prosafe/item-wise-purchase-chart`,
                {
                    firm_id: localStorage.getItem('PAY_USER_FIRM_ID'),
                    year_id: localStorage.getItem('PAY_USER_YEAR_ID'),
                    project_id: project || undefined,
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem('PAY_USER_TOKEN')}` } }
            );
            setData(res?.data?.success ? (res.data.data || { total_purchased_qty: 0, total_items_count: 0, items: [] }) : { total_purchased_qty: 0, total_items_count: 0, items: [] });
        } catch {
            setData({ total_purchased_qty: 0, total_items_count: 0, items: [] });
        } finally {
            setLoading(false);
        }
    }, [project]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const pieData = useMemo(() => {
        const items = data?.items || [];
        if (!items.length) return [];
        const sorted = [...items].sort((a, b) => b.total_qty - a.total_qty);
        if (sorted.length <= TOP_N_PIE) return sorted.map(it => ({ ...it, displayName: it.item_name }));
        const topItems = sorted.slice(0, TOP_N_PIE).map(it => ({ ...it, displayName: it.item_name }));
        const others = sorted.slice(TOP_N_PIE);
        const oQty = others.reduce((s, it) => s + Number(it.total_qty || 0), 0);
        const oAmt = others.reduce((s, it) => s + Number(it.total_amount || 0), 0);
        const total = items.reduce((s, it) => s + Number(it.total_qty || 0), 0);
        const oPct = total > 0 ? Number(((oQty / total) * 100).toFixed(1)) : 0;
        topItems.push({
            displayName: `Others (${others.length} items)`, item_name: `Others (${others.length} items)`,
            item_id: '__others__', item_code: '', unit: '',
            total_qty: Number(oQty.toFixed(2)), total_amount: Number(oAmt.toFixed(2)),
            percentage: oPct, _isOthers: true, _othersCount: others.length,
        });
        return topItems;
    }, [data]);

    const totalQty = data?.total_purchased_qty || 0;
    const totalItems = data?.total_items_count || 0;
    const selectedProjName = project ? (projectOptions.find(p => p.value === project)?.label || 'Project') : 'All Projects';

    const allItems = useMemo(() => {
        const items = data?.items || [];
        if (!items.length) return [];
        return [...items].sort((a, b) => b.total_qty - a.total_qty);
    }, [data]);

    const handleDownload = () => {
        downloadPieChartWithLeaderLines(
            pieData,
            `item_wise_purchase_chart_${project ? 'project' : 'all'}.png`,
            `Item Wise Purchase Receiving — ${selectedProjName}`,
            totalQty,
            totalItems,
            allItems
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div>
                    <h5 style={{ fontWeight: 700, color: '#0f172a', margin: 0, fontSize: '0.95rem' }}>Item Wise Purchase</h5>
                    <p style={{ color: '#94a3b8', fontSize: 11.5, margin: '2px 0 0' }}>Qty % share across purchases</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                    <Dropdown value={project} options={projectOptions} onChange={(e) => setProject(e.value || null)}
                        placeholder="All Projects" filter showClear={Boolean(project)} className="p-inputtext-sm"
                        style={{ minWidth: 175, height: 38, borderRadius: 8, fontSize: 13, display: 'inline-flex', alignItems: 'center' }} />
                    <button type="button" onClick={handleDownload} disabled={loading || pieData.length === 0} title="Download Chart (PNG)"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 8,
                            background: pieData.length > 0 ? '#f8fafc' : '#f8fafc',
                            border: `1px solid ${pieData.length > 0 ? '#cbd5e1' : '#e2e8f0'}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: pieData.length > 0 ? 'pointer' : 'not-allowed',
                            color: pieData.length > 0 ? '#334155' : '#cbd5e1',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            transition: 'all 0.15s ease',
                            flexShrink: 0,
                        }}>
                        <Download size={16} />
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Package size={12} style={{ color: '#0284c7' }} />
                    <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>{Number(totalQty).toLocaleString('en-IN')} Total Qty</span>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Layers size={12} style={{ color: '#16a34a' }} />
                    <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{totalItems} Items</span>
                </div>
            </div>

            {/* Chart */}
            <div style={{ flex: 1, minHeight: 0 }} ref={chartContainerRef}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300 }}>
                        <div className="spinner-border" style={{ width: '2rem', height: '2rem', color: '#ff6700' }} role="status" />
                        <p style={{ color: '#94a3b8', marginTop: 12, fontSize: 13 }}>Loading…</p>
                    </div>
                ) : pieData.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300, background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
                        <HelpCircle size={40} style={{ color: '#cbd5e1', marginBottom: 10 }} />
                        <span style={{ color: '#94a3b8', fontSize: 13 }}>No purchase data found</span>
                    </div>
                ) : (
                    <>
                        <p style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 11, margin: '0 0 2px' }}>
                            Hover a slice for details{totalItems > TOP_N_PIE && ` · top ${TOP_N_PIE} shown, ${totalItems - TOP_N_PIE} grouped`}
                        </p>
                        <ResponsiveContainer width="100%" height={340}>
                            <PieChart>
                                <Pie
                                    data={pieData} dataKey="total_qty" nameKey="displayName"
                                    cx="50%" cy="50%" outerRadius={120} innerRadius={0}
                                    paddingAngle={pieData.length > 1 ? 1.5 : 0}
                                    activeIndex={activeIndex} activeShape={renderActiveShape}
                                    onMouseEnter={(_, i) => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)}
                                    animationBegin={0} animationDuration={700} animationEasing="ease-out" isAnimationActive
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${entry.item_id || index}`}
                                            fill={entry._isOthers ? OTHERS_COLOR : PIE_COLORS[index % PIE_COLORS.length]}
                                            stroke="#fff" strokeWidth={2} style={{ cursor: 'pointer' }} />
                                    ))}
                                </Pie>
                                <Tooltip content={<PieTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </>
                )}
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
// BAR CHART PANEL — Item Wise Issue (Approved Only)
// ══════════════════════════════════════════════════════════════════════════════
const BarIssuePanel = ({ projectOptions }) => {
    const [project, setProject] = useState(null);
    const [data, setData] = useState(null);   // { items: [], total_items: 0 }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const chartContainerRef = useRef(null);

    // Transform API data → recharts format (top 20 sorted by total_qty desc)
    const chartData = useMemo(() => {
        if (!data?.items?.length) return [];
        return data.items.slice(0, TOP_N_BAR).map(it => ({
            name: it.item_name.length > 16 ? it.item_name.slice(0, 14) + '…' : it.item_name,
            fullName: it.item_name,
            item_code: it.item_code,
            unit: it.unit,
            total_qty: it.total_qty,
            total_amount: it.total_amount,
            issue_count: it.issue_count,
        }));
    }, [data]);

    const fetchData = useCallback(async () => {
        if (!project) return;
        try {
            setLoading(true);
            setError(null);
            const res = await axios.post(
                `${V_URL}/prosafe/item-usage-bar-chart`,
                {
                    firm_id: localStorage.getItem('PAY_USER_FIRM_ID'),
                    year_id: localStorage.getItem('PAY_USER_YEAR_ID'),
                    project_id: project,
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem('PAY_USER_TOKEN')}` } }
            );
            if (res?.data?.success) {
                setData(res.data.data);
            } else {
                setData({ items: [], total_items: 0 });
            }
        } catch (e) {
            setError('Failed to load data. Please try again.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [project]);

    useEffect(() => {
        if (project) fetchData();
        else setData(null);
    }, [project, fetchData]);

    const totalItems = data?.total_items || 0;
    const totalIssuedQty = useMemo(() => {
        if (!data?.items?.length) return 0;
        return data.items.reduce((s, it) => s + (Number(it.total_qty) || 0), 0);
    }, [data]);
    const selectedLabel = project ? (projectOptions.find(p => p.value === project)?.label || 'Project') : '';

    const handleDownload = () => {
        downloadChartAsPng(
            chartContainerRef,
            `item_wise_issue_chart_${project || 'project'}.png`,
            `Item Wise Issue (Approved) — ${selectedLabel}`
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div>
                    <h5 style={{ fontWeight: 700, color: '#0f172a', margin: 0, fontSize: '0.95rem' }}>
                        Item Wise Issue <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 12, marginLeft: 6 }}>Approved</span>
                    </h5>
                    <p style={{ color: '#94a3b8', fontSize: 11.5, margin: '2px 0 0' }}>Approved issued item quantities for selected project</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                    <Dropdown value={project} options={projectOptions}
                        onChange={(e) => setProject(e.value || null)}
                        placeholder="Select Project *" filter showClear={Boolean(project)} className="p-inputtext-sm"
                        style={{ minWidth: 185, height: 38, borderRadius: 8, fontSize: 13, display: 'inline-flex', alignItems: 'center' }} />
                    <button type="button" onClick={handleDownload} disabled={loading || !chartData.length} title="Download Chart (PNG)"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 8,
                            background: chartData.length > 0 ? '#f8fafc' : '#f8fafc',
                            border: `1px solid ${chartData.length > 0 ? '#cbd5e1' : '#e2e8f0'}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: chartData.length > 0 ? 'pointer' : 'not-allowed',
                            color: chartData.length > 0 ? '#334155' : '#cbd5e1',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            transition: 'all 0.15s ease',
                            flexShrink: 0,
                        }}>
                        <Download size={16} />
                    </button>
                </div>
            </div>

            {/* KPIs — only when data is loaded */}
            {data && project && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <ArrowUpRight size={12} style={{ color: '#0284c7' }} />
                        <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>
                            {Number(totalIssuedQty).toLocaleString('en-IN')} Total Issued Qty
                        </span>
                    </div>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Layers size={12} style={{ color: '#16a34a' }} />
                        <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{totalItems} Distinct Items</span>
                    </div>
                    {totalItems > TOP_N_BAR && (
                        <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Info size={12} style={{ color: '#b45309' }} />
                            <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                                Showing top {TOP_N_BAR} of {totalItems} items
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Chart area */}
            <div style={{ flex: 1, minHeight: 0 }} ref={chartContainerRef}>
                {/* No project selected */}
                {!project ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 380, background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 14, border: '2px dashed #e2e8f0' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                            <FolderOpen size={28} style={{ color: '#94a3b8' }} />
                        </div>
                        <h6 style={{ fontWeight: 700, color: '#475569', marginBottom: 8, fontSize: '1rem' }}>Select a Project</h6>
                        <p style={{ color: '#94a3b8', fontSize: 13, maxWidth: 320, textAlign: 'center', lineHeight: 1.65, margin: 0 }}>
                            Choose a project from the dropdown above to view all approved issued items and their quantities.
                        </p>
                    </div>

                ) : loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 380 }}>
                        <div className="spinner-border" style={{ width: '2rem', height: '2rem', color: '#2563eb' }} role="status" />
                        <p style={{ color: '#94a3b8', marginTop: 12, fontSize: 13 }}>Fetching approved issue data…</p>
                    </div>

                ) : error ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 380, background: '#fef2f2', borderRadius: 14, border: '2px dashed #fecaca' }}>
                        <HelpCircle size={40} style={{ color: '#f87171', marginBottom: 10 }} />
                        <span style={{ color: '#ef4444', fontSize: 13, fontWeight: 600 }}>{error}</span>
                    </div>

                ) : !data || chartData.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 380, background: '#f8fafc', borderRadius: 14, border: '2px dashed #e2e8f0' }}>
                        <HelpCircle size={40} style={{ color: '#cbd5e1', marginBottom: 10 }} />
                        <h6 style={{ fontWeight: 700, color: '#64748b', marginBottom: 6 }}>No Issued Items Found</h6>
                        <p style={{ color: '#94a3b8', fontSize: 13, maxWidth: 300, textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
                            No approved issue transactions found for <strong>{selectedLabel}</strong>.
                        </p>
                    </div>

                ) : (
                    /* ── Vertical Bar Chart ── */
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <ResponsiveContainer width="100%" height={360}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 12, right: 16, left: 0, bottom: 80 }}
                                barSize={chartData.length <= 6 ? 38 : chartData.length <= 12 ? 26 : 18}
                            >
                                <defs>
                                    <linearGradient id="issueBarGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.95} />
                                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.7} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 11, fill: '#64748b', fontWeight: 500 }}
                                    interval={0}
                                    angle={-38}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => Number(v).toLocaleString('en-IN')}
                                />
                                <Tooltip content={<IssueBarTooltip />} cursor={{ fill: 'rgba(37, 99, 235, 0.06)', radius: 6 }} />
                                <Bar
                                    dataKey="total_qty"
                                    fill="url(#issueBarGradient)"
                                    radius={[6, 6, 0, 0]}
                                    isAnimationActive
                                    animationDuration={650}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT — two-panel layout
// ══════════════════════════════════════════════════════════════════════════════
const ItemWisePurchaseChart = () => {
    const projectOptions = useProjectOptions();
    return (
        <div className="card border-0 mb-4" style={{ borderRadius: 14, boxShadow: '0 2px 18px rgba(0,0,0,0.07)' }}>
            <div className="card-body" style={{ padding: '22px 24px' }}>
                {/* Card heading */}
                <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
                    <h4 style={{ fontWeight: 700, color: '#ff6700', margin: 0, fontSize: '1.15rem' }}>Store Item Analytics</h4>
                    <p style={{ color: '#94a3b8', fontSize: 12.5, margin: '3px 0 0' }}>
                        Purchase receiving item percentages &amp; approved issued item quantities by project
                    </p>
                </div>

                {/* Two-panel row */}
                <div className="row g-0">
                    {/* Left — Pie chart */}
                    <div className="col-12 col-xl-4" style={{ paddingRight: 20, borderRight: '1px solid #f1f5f9' }}>
                        <PiePurchasePanel projectOptions={projectOptions} />
                    </div>
                    {/* Right — Bar chart */}
                    <div className="col-12 col-xl-8" style={{ paddingLeft: 24 }}>
                        <BarIssuePanel projectOptions={projectOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ItemWisePurchaseChart;
